export const BROADCAST_CHANNEL_NAME = 'documint-tab-sync';

export type TabSyncMessage =
  | { type: 'logout' }
  | { type: 'unpushed-write-count-request'; requestId: string }
  | { type: 'unpushed-write-count-response'; requestId: string; count: number | undefined };

class TabSyncService {
  private broadcastChannel: BroadcastChannel;

  constructor() {
    this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  }

  private subscribe<Type extends TabSyncMessage['type']>(
    type: Type,
    callback: (message: Extract<TabSyncMessage, { type: Type }>) => void
  ): () => void {
    const handler = (event: MessageEvent<TabSyncMessage>) => {
      const message = event.data;
      if (message.type === type) {
        callback(message as Extract<TabSyncMessage, { type: Type }>);
      }
    };

    this.broadcastChannel.addEventListener('message', handler);
    return () => this.broadcastChannel.removeEventListener('message', handler);
  }

  async broadcastLogout(): Promise<void> {
    this.broadcastChannel.postMessage({ type: 'logout' } satisfies TabSyncMessage);
  }

  subscribeToLogout(callback: () => void): () => void {
    return this.subscribe('logout', () => callback());
  }

  /**
   * Asks the other tabs how many local writes are still unpushed. Only the RxDB leader tab runs
   * replication, so only it can count; the others stay silent. Resolves undefined when no tab
   * answers within `timeoutMs` (or the channel is already closed). A channel never delivers a
   * message back to its sender, so a leader must count itself instead of asking.
   */
  requestUnpushedWriteCount(timeoutMs: number): Promise<number | undefined> {
    const requestId = crypto.randomUUID();

    return new Promise((resolve) => {
      const finish = (count: number | undefined) => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(count);
      };
      const timeout = setTimeout(() => finish(undefined), timeoutMs);
      const unsubscribe = this.subscribe('unpushed-write-count-response', (message) => {
        if (message.requestId === requestId) {
          finish(message.count);
        }
      });

      try {
        this.broadcastChannel.postMessage({ type: 'unpushed-write-count-request', requestId } satisfies TabSyncMessage);
      } catch {
        finish(undefined);
      }
    });
  }

  // The answering side of requestUnpushedWriteCount: `count` resolves null when this tab is not the
  // one that can answer, and nothing is sent.
  answerUnpushedWriteCountRequests(count: () => Promise<number | undefined | null>): () => void {
    return this.subscribe('unpushed-write-count-request', async ({ requestId }) => {
      const answer = await count();
      if (answer === null) {
        return;
      }

      this.broadcastChannel.postMessage({
        type: 'unpushed-write-count-response',
        requestId,
        count: answer,
      } satisfies TabSyncMessage);
    });
  }

  async cleanup(): Promise<void> {
    this.broadcastChannel.close();
  }
}

export const tabSyncService = new TabSyncService();
